import { ReactNode } from "react";

import ErrorBanner from "components/ui/ErrorBanner";
import { toErrorDetails } from "services/errors";
import type { ErrorDetail, ErrorMessages } from "services/errors";

type Props = {
  error?: unknown;
  messages?: ErrorMessages;
  onRetry?: () => void;
  className?: string;
};

/**
 * One banner per message the error carries, each looked up in `messages` by its own
 * `error_code` before falling back to the server's wording.
 *
 * A falsy `error` renders nothing, so callers need no `error &&` guard of their own —
 * and the empty string several components hold as "no error yet" reads as one.
 *
 * Outside `components/ui/` because it reads the app's error layer — `toErrorDetails`
 * and the `error_code` overrides — which the kit has no business knowing. With no
 * failed request behind the message, render `ErrorBanner` directly.
 */
export default function ApiErrorBanner(props: Props) {
  if (!props.error) {
    return null;
  }

  const details = toErrorDetails(props.error);

  return (
    <>
      {details.map((detail, index) => (
        <ErrorBanner
          key={index}
          className={props.className}
          // Retrying acts on the request rather than on any one of its messages,
          // so the action appears once, under the last.
          onRetry={index === details.length - 1 ? props.onRetry : undefined}
        >
          {resolveMessage(detail, props.messages)}
        </ErrorBanner>
      ))}
    </>
  );
}

function resolveMessage(detail: ErrorDetail, messages?: ErrorMessages): ReactNode {
  const error = detail.error;

  if (error?.error_code) {
    const override = messages?.[error.error_code];
    if (override !== undefined) {
      return typeof override === "function" ? override(error) : override;
    }
  }

  return detail.text;
}
